import { useMatch, useNavigate } from "react-router-dom";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { CampaignsTab } from "./CampaignsTab";
import { EmailBuilder } from "./EmailBuilder";
import { FormBuilder } from "./FormBuilder";
import { ListsTab } from "./ListsTab";
import { SequencesTab } from "./SequencesTab";
import { WorkflowsTab } from "./WorkflowsTab";

/**
 * Marketing - the tenant's capture and outreach tooling.
 * Forms: build lead-capture forms and embed them on any site.
 * Emails: design branded, CAN-SPAM-compliant email templates.
 */
const TABS = ["forms", "emails", "lists", "sequences", "workflows", "campaigns"] as const;

const NOT_ENABLED = (label: string) => (
  <p className="text-sm text-muted-foreground">
    {label} isn't turned on for this workspace yet.
  </p>
);

export const MarketingPage = () => {
  const navigate = useNavigate();
  const { moduleFlags } = useConfigurationContext();
  const formsVisible = moduleFlags?.forms !== false;
  const sequencesVisible = moduleFlags?.sequences !== false;
  const workflowsVisible = moduleFlags?.workflows !== false;
  const campaignsVisible = moduleFlags?.campaigns !== false;
  const tabMatch = useMatch("/marketing/:tab");
  const param = tabMatch?.params?.tab ?? "forms";
  const currentTab = (TABS as readonly string[]).includes(param) ? param : "forms";

  return (
  <div className="max-w-6xl mx-auto px-4 py-6 flex flex-col gap-4">
    <h1 className="text-xl font-semibold">Marketing</h1>
    <Tabs
      value={currentTab}
      onValueChange={(value) =>
        navigate(value === "forms" ? "/marketing" : `/marketing/${value}`)
      }
    >
      <TabsList>
        {formsVisible && <TabsTrigger value="forms">Forms</TabsTrigger>}
        <TabsTrigger value="emails">Emails</TabsTrigger>
        <TabsTrigger value="lists">Lists</TabsTrigger>
        {sequencesVisible && (
          <TabsTrigger value="sequences">Sequences</TabsTrigger>
        )}
        {workflowsVisible && (
          <TabsTrigger value="workflows">Workflows</TabsTrigger>
        )}
        {campaignsVisible && (
          <TabsTrigger value="campaigns">Campaigns</TabsTrigger>
        )}
      </TabsList>
      <TabsContent value="forms" className="pt-4">
        {formsVisible ? <FormBuilder /> : NOT_ENABLED("Forms")}
      </TabsContent>
      <TabsContent value="emails" className="pt-4">
        <EmailBuilder />
      </TabsContent>
      <TabsContent value="lists" className="pt-4">
        <ListsTab />
      </TabsContent>
      <TabsContent value="sequences" className="pt-4">
        {sequencesVisible ? <SequencesTab /> : NOT_ENABLED("Sequences")}
      </TabsContent>
      <TabsContent value="workflows" className="pt-4">
        {workflowsVisible ? <WorkflowsTab /> : NOT_ENABLED("Workflows")}
      </TabsContent>
      <TabsContent value="campaigns" className="pt-4">
        {campaignsVisible ? <CampaignsTab /> : NOT_ENABLED("Campaigns")}
      </TabsContent>
    </Tabs>
  </div>
  );
};

MarketingPage.path = "/marketing";
